/**
 * consent-page.tsx — Màn đồng ý OAuth của "Kết nối AI (Claude)" (`/oauth/consent?request=<phiếu>`).
 *
 * Claude (hoặc ứng dụng MCP khác) đưa người dùng tới /mcp/oauth/authorize; backend kiểm client + PKCE rồi chuyển về
 * đây kèm phiếu `request`. Màn nằm trong ProtectedRoute (chưa đăng nhập → /login rồi quay lại đúng URL kèm query)
 * nhưng NGOÀI AppLayout — một hộp giữa màn như trang đăng nhập. Bấm Cho phép / Từ chối → backend trả `redirectTo`
 * (về Claude / localhost kèm mã hoặc lỗi) và trình duyệt chuyển hẳn sang đó.
 * Quyền: `ecdp.read` luôn cấp; `ecdp.write` / `ecdp.pii` chỉ chọn được khi tổ chức cho phép (Cài đặt → Kết nối AI).
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, BadgeCheck, Laptop, Loader2, ShieldAlert } from 'lucide-react'
import { BrandLogo } from '@/components/shared/brand-logo'
import { Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/misc'
import { useMcpConsentDecision, useMcpConsentRequest, type McpConsentRequest } from '@/hooks/use-mcp-connector'
import { useAuthStore } from '@/stores/auth-store'
import { apiError, apiErrorCode } from '@/lib/api-client'
import {
  MCP_BASE_SCOPE, initialMcpScopes, mcpClientKind, toggleMcpScope, type McpScopeKey,
} from '@/lib/mcp-connector'

export function ConsentPage() {
  const [params] = useSearchParams()
  const ticket = params.get('request') ?? ''
  const { data, isLoading, error } = useMcpConsentRequest(ticket)

  // Chống nhúng khung (clickjacking): màn đồng ý bị nhúng trong trang khác thì không hiện nút (rà soát 24/10, L9 — nginx
  // production cũng phải gửi frame-ancestors 'none').
  const framed = typeof window !== 'undefined' && window.top !== window.self

  let body: React.ReactNode
  if (framed) {
    body = <p className="text-center text-sm">Hãy mở trang cho phép kết nối trực tiếp trong trình duyệt, không qua trang khác.</p>
  } else if (!ticket || error) {
    // Phiếu hỏng / hết hạn (REQUEST_INVALID) → không có chỗ nào để quay về; bảo người dùng kết nối lại từ Claude.
    const invalid = !ticket || apiErrorCode(error) === 'REQUEST_INVALID'
    body = (
      <div className="space-y-2 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-destructive" />
        <h1 className="text-base font-medium">
          {invalid ? 'Yêu cầu kết nối không hợp lệ hoặc đã hết hạn' : 'Không tải được yêu cầu kết nối'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {invalid
            ? 'Hãy quay lại Claude và bấm kết nối eCDP lại từ đầu.'
            : apiError(error)}
        </p>
      </div>
    )
  } else if (isLoading || !data) {
    body = <Loading label="Đang tải yêu cầu kết nối…" />
  } else {
    // key theo phiếu → đổi phiếu thì lựa chọn quyền khởi tạo lại.
    body = <ConsentForm key={ticket} ticket={ticket} req={data} />
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-8">
      <div className="w-full max-w-md space-y-5 rounded-xl border bg-card p-6 shadow-sm">
        <BrandLogo className="justify-center" />
        {body}
      </div>
    </div>
  )
}

function ConsentForm({ ticket, req }: { ticket: string; req: McpConsentRequest }) {
  const decide = useMcpConsentDecision()
  const [selected, setSelected] = useState<McpScopeKey[]>(() => initialMcpScopes(req.scopes))
  // Đang chuyển sang Claude — giữ nút khoá tới khi trang rời đi.
  const [leaving, setLeaving] = useState(false)
  const kind = mcpClientKind(req.client)
  const busy = decide.isPending || leaving

  // Đăng xuất → ProtectedRoute đưa về /login và quay lại ĐÚNG màn đồng ý này (phiếu `request` còn nguyên trên URL) sau khi đăng nhập tài khoản khác.
  const logout = useAuthStore((s) => s.logout)
  const switchAccount = async () => {
    setLeaving(true)
    await logout()
  }

  const submit = async (approve: boolean) => {
    try {
      const res = await decide.mutateAsync({ request: ticket, approve, scopes: approve ? selected : undefined })
      setLeaving(true)
      window.location.assign(res.redirectTo)
    } catch (e) {
      toast.error(apiErrorCode(e) === 'MCP_DISABLED' ? 'Tổ chức đã tắt Kết nối AI — không thể cho phép lúc này.' : apiError(e))
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5 text-center">
        <h1 className="text-base font-medium">
          <span className="font-semibold">{req.client.name}</span> muốn truy cập eCDP của{' '}
          <span className="font-semibold">{req.org.name}</span>
        </h1>
        <p className="text-xs text-muted-foreground">Sẽ chuyển về: <code>{req.client.redirectHost}</code></p>
        {kind === 'official' && (
          <>
            <Badge variant="success" className="gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Claude (Anthropic)</Badge>
            {/* Đường dẫn cho phép có thể bị kẻ khác gửi cho bạn — chỉ cho phép khi chính bạn vừa bấm kết nối (rà soát 24/10, L2). */}
            <p className="text-xs text-muted-foreground">Chỉ cho phép nếu chính bạn vừa bấm kết nối eCDP trong Claude.</p>
          </>
        )}
      </div>

      <div className="space-y-1.5 rounded-lg border bg-muted/40 p-3 text-xs" data-testid="consent-workspace">
        <p>
          <span className="text-muted-foreground">Workspace:</span> <span className="font-semibold">{req.org.name || '(chưa đặt tên)'}</span>{' '}
          <code className="text-muted-foreground">{req.org.id.slice(0, 8)}</code>
        </p>
        <p className="text-muted-foreground">
          {req.org.contactCount.toLocaleString('vi-VN')} khách · {req.org.agentCount.toLocaleString('vi-VN')} nhân viên AI
        </p>
        <p>
          <span className="text-muted-foreground">Tài khoản:</span> {req.account.fullName ? `${req.account.fullName} · ` : ''}{req.account.email}
        </p>
        <button
          type="button"
          className="font-medium text-primary hover:underline disabled:opacity-50"
          disabled={busy}
          onClick={() => void switchAccount()}
        >
          Không phải workspace này? Đăng xuất để dùng tài khoản khác
        </button>
      </div>

      {kind === 'local' && (
        // Đặc tả MCP khuyến nghị cảnh báo thêm khi chỉ quay về localhost: tiến trình nào trên máy cũng có thể giả danh ứng dụng.
        <p className="flex gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs">
          <Laptop className="h-4 w-4 shrink-0 text-warning" />
          Ứng dụng chạy trên máy của bạn (ví dụ Claude Code) — kết quả trả về chính máy này. Chỉ cho phép nếu chính bạn vừa
          thêm eCDP trong ứng dụng đó trên máy này.
        </p>
      )}
      {kind === 'unknown' && (
        <p className="flex gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          Ứng dụng này không phải Claude chính chủ. Chỉ cho phép nếu bạn tin ứng dụng này.
        </p>
      )}

      {!req.connector.enabled ? (
        // Tổ chức chưa bật connector → không cho phép được; chỉ từ chối (trả lỗi về ứng dụng).
        <div className="space-y-1.5 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <p className="font-medium">Tổ chức chưa bật Kết nối AI (Claude)</p>
          {req.connector.canEnable ? (
            <p className="text-xs text-muted-foreground">
              Bật ở{' '}
              <Link to="/settings?m=ai-connector" className="font-medium text-primary hover:underline">
                Cài đặt → Kết nối AI (Claude)
              </Link>{' '}
              rồi quay lại Claude kết nối lại.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Nhờ quản trị viên của tổ chức bật trong Cài đặt → Kết nối AI (Claude).</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-medium">Quyền được cấp</p>
          <ul className="space-y-2">
            {req.scopes.map((s) => {
              const base = s.key === MCP_BASE_SCOPE
              const id = `scope-${s.key}`
              return (
                <li key={s.key} className="flex items-start gap-2">
                  <Checkbox
                    id={id}
                    className="mt-0.5"
                    checked={base || selected.includes(s.key)}
                    disabled={base || !s.allowed || busy}
                    onCheckedChange={(v) => setSelected((cur) => toggleMcpScope(cur, s, v === true))}
                  />
                  <label htmlFor={id} className={`text-sm ${!s.allowed && !base ? 'text-muted-foreground' : ''}`}>
                    {s.label}
                    {!s.allowed && !base && <span className="block text-xs">Tổ chức chưa cho phép</span>}
                  </label>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Dữ liệu bạn cho phép sẽ được gửi tới tài khoản Claude của bạn (Anthropic) mỗi khi Claude dùng eCDP, trong phạm vi
        quyền của chính bạn. Bạn có thể thu hồi bất cứ lúc nào ở Cài đặt → Kết nối AI (Claude).
      </p>

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" disabled={busy} onClick={() => submit(false)}>
          Từ chối
        </Button>
        <Button className="flex-1" disabled={busy || !req.connector.enabled} onClick={() => submit(true)}>
          {busy && <Loader2 className="animate-spin" />}
          Cho phép
        </Button>
      </div>
    </div>
  )
}
